import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, Search, ChevronDown, Mail, Flag } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import { settingsService } from '../../services/settings/settings.service';
import { cn } from '../../utils/helpers';

export default function Help() {
  const faqs = useMemo(() => settingsService.getFaqs(), []);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const categories = Array.from(new Set(faqs.map((f) => f.category)));
  const [activeCat, setActiveCat] = useState<string>('All');

  const filtered = faqs.filter((f) => {
    const matchesQuery = (f.question + f.answer).toLowerCase().includes(query.toLowerCase());
    const matchesCat = activeCat === 'All' || f.category === activeCat;
    return matchesQuery && matchesCat;
  });

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeader title="Help center" subtitle="Answers to common questions" icon={HelpCircle} backTo="/app/settings/support" />

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help articles"
          className="w-full pl-11 pr-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {['All', ...categories].map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCat(cat)}
            className={cn('px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all', activeCat === cat ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700')}
          >
            {cat}
          </button>
        ))}
      </div>

      <SettingCard flush>
        {filtered.map((f) => {
          const isOpen = open === f.id;
          return (
            <div key={f.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
              <button onClick={() => setOpen(isOpen ? null : f.id)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                <span className="font-semibold text-sm text-neutral-900 dark:text-neutral-50">{f.question}</span>
                <ChevronDown className={cn('w-5 h-5 text-neutral-400 dark:text-neutral-500 transition-transform shrink-0', isOpen && 'rotate-180')} />
              </button>
              {isOpen && <p className="px-5 pb-4 text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">{f.answer}</p>}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="px-5 py-8 text-center text-sm text-neutral-400 dark:text-neutral-500">No articles found. Try contacting support.</p>}
      </SettingCard>

      <SettingCard title="Still need help?" flush>
        <Link to="/app/settings/support/contact" className="flex items-center gap-3 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
          <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Mail className="w-5 h-5" /></div>
          <div><p className="font-semibold text-sm">Contact support</p><p className="text-xs text-neutral-500 dark:text-neutral-400">Get a personal reply</p></div>
        </Link>
        <Link to="/app/settings/support/report" className="flex items-center gap-3 px-5 py-4 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
          <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Flag className="w-5 h-5" /></div>
          <div><p className="font-semibold text-sm">Report a problem</p><p className="text-xs text-neutral-500 dark:text-neutral-400">Tell us what went wrong</p></div>
        </Link>
      </SettingCard>
    </div>
  );
}
